import { useMemo } from "react";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";

import queryString from "query-string";

import { Button, ButtonsWrapper } from "components/elements";
import { PageContent, PageHeader, Section, TableBody } from "components/layout";
import { RestrictedArea, ROLES } from "containers/Permissions";
import { TabbedTable } from "containers/TabbedTable";
import { getOems, TiVoDeviceOem, usePlatforms } from "hooks/tivoDevices";
import { pathToUrl } from "utils/url";

import { PATHS } from "../constants";
import { TiVoDeviceRow } from "./components/TiVoDeviceRow";
import { INIT_TAB, PAGE_LIMIT, TEXTS } from "./constants";
import styles from "./TiVoDevices.scss";

const paginateOems = (
  oems: TiVoDeviceOem[],
  page: number = 1
): TiVoDeviceOem[] => {
  return oems.slice((page - 1) * PAGE_LIMIT, page * PAGE_LIMIT);
};

export function TiVoDevicesList() {
  const location = useLocation();
  const { page = 1 } = queryString.parse(location.search);
  const { tabId = INIT_TAB } = useParams();
  const {
    data,
    isLoading: loading,
    error,
  } = usePlatforms({
    include: ["oems", "oems.clientTypes"],
  });

  const platformId = useMemo(
    () =>
      tabId === INIT_TAB && data?.results.length ? data.results[0].id : tabId,
    [data, tabId]
  );

  const oems = useMemo(() => {
    if (data?.results.length) {
      return getOems(data?.results, {
        platforms: [platformId],
      });
    }
    return [];
  }, [data?.results, platformId]);

  const paginatedOems = useMemo(
    () => paginateOems(oems, Number(page)),
    [oems, page]
  );

  const tabsConfig = useMemo(() => {
    if (loading) {
      return [];
    }
    return data?.results.map((platform) => ({
      id: platform.id,
      label: platform.name,
      columns: [
        {
          id: "name",
          label: TEXTS.NAME_COL,
          className: styles.nameCol,
        },
        {
          id: "slug",
          label: TEXTS.SLUG_COL,
        },
        {
          id: "options",
          label: "",
          className: styles.actionsCol,
        },
      ],
      dataTestId: platform.slug,
    }));
  }, [data?.results, loading]);

  function renderTableBody(oems: TiVoDeviceOem[]) {
    return (
      <TableBody>
        {oems.map((oem) => (
          <TiVoDeviceRow platformId={platformId} oem={oem} key={oem.id} />
        ))}
      </TableBody>
    );
  }

  return (
    <>
      <PageHeader>{TEXTS.HEADER}</PageHeader>
      <PageContent>
        <Section>
          <RestrictedArea
            allowed={[ROLES.tivoDevices.admin]}
            showFallback={false}
          >
            <ButtonsWrapper>
              <Button
                to={pathToUrl(PATHS.createOem, { platformId })}
                type={"green"}
              >
                {TEXTS.OEM_CREATE_BTN}
              </Button>
            </ButtonsWrapper>
          </RestrictedArea>
          <TabbedTable
            data={{
              count: oems.length,
              error,
              loading,
              results: paginatedOems,
            }}
            preservePageParam={false}
            renderTableBody={renderTableBody}
            rowsPerPage={PAGE_LIMIT}
            tabsConfig={tabsConfig}
          />
        </Section>
      </PageContent>
    </>
  );
}
